<template>
    <div class="ImportForm">
        <div class="Form mb-2">
            <div class="FormField mb-2">
                <WText
                    tag="label"
                    typo="label"
                    :size="14"
                >
                    {{ $t('imports.create.form.source.label') }}
                </WText>
                <WFieldError
                    :validator="$v.form.source"
                    :label="$t('imports.create.form.source.label')"
                    #default="{ errorMessage, listeners }"
                >
                    <WSelect
                        v-model="form.source"
                        name="source"
                        :error="errorMessage"
                        :placeholder="$t('imports.create.form.source.placeholder')"
                        :options="sources"
                        :disabled="isLoading"
                        v-on="listeners"
                        @input="handleChangeSource"
                    />
                </WFieldError>
            </div>

            <div class="mb-2">
                <WText
                    tag="label"
                    typo="label"
                    :size="14"
                >
                    {{ $t('imports.create.form.entity.label') }}
                </WText>
                <div class="flex row-left-center">
                    <WFieldError
                        :validator="$v.form.entity"
                        :label="$t('imports.create.form.entity.label')"
                        #default="{ errorMessage, listeners }"
                    >
                        <WSelect
                            v-model="form.entity"
                            name="entity"
                            :error="errorMessage"
                            :placeholder="isMultiEntity ? 'Auto-detected from sheets' : $t('imports.create.form.entity.placeholder')"
                            :options="entities"
                            :disabled="isLoading || disabledEntity"
                            v-on="listeners"
                        />
                    </WFieldError>
                    <WLink
                        v-if="!isMultiEntity"
                        href="#"
                        :disabled="!form.entity || isDownloading"
                        class="ml-1"
                        @click="handleDownloadTemplate"
                    >
                        <WText
                            tag="span"
                            :color="!form.entity ? 'secondary' : 'primary'"
                            :size="14"
                        >
                            {{ $t('imports.create.downloadTemplate') }}
                        </WText>
                    </WLink>
                    <WLink
                        v-if="isMultiEntity"
                        href="#"
                        :disabled="isDownloading"
                        class="ml-1"
                        @click="handleDownloadMultiEntityTemplate"
                    >
                        <WText
                            tag="span"
                            color="primary"
                            :size="14"
                        >
                            {{ $t('imports.create.downloadTemplate') }}
                        </WText>
                    </WLink>
                </div>
                <WText
                    v-if="isMultiEntity"
                    tag="p"
                    :size="12"
                    color="secondary"
                    class="mt-1 mb-0"
                >
                    Entities will be auto-detected from sheet names: General Partners, Funds, Funds Performance, Companies, Companies Evolution, Transactions, Transactions Performance.
                </WText>
            </div>

            <div class="FormField mb-2">
                <WText
                    tag="label"
                    typo="label"
                    :size="14"
                >
                    {{ $t('imports.create.form.name.label') }}
                </WText>
                <WFieldError
                    :validator="$v.form.name"
                    :label="$t('imports.create.form.name.label')"
                    #default="{ errorMessage, listeners }"
                >
                    <WInput
                        v-model="form.name"
                        :error="errorMessage"
                        :placeholder="$t('imports.create.form.name.placeholder')"
                        :disabled="isLoading"
                        type="text"
                        name="name"
                        v-on="listeners"
                    />
                </WFieldError>
            </div>

            <div class="FormFile mb-2">
                <WText
                    tag="label"
                    typo="label"
                    :size="14"
                >
                    {{ $t('imports.create.form.file.label') }}
                </WText>
                <WFieldError
                    :validator="$v.form.file"
                    :label="$t('imports.create.form.file.label')"
                    #default="{ errorMessage, listeners }"
                >
                    <WFile
                        id="uploader"
                        :accept="fileAccept"
                        :error="errorMessage"
                        name="file"
                        #label="{ file }"
                        @input="handleFile"
                        v-on="listeners"
                    >
                        <WText
                            weight="semi"
                            ellipsis
                            class="mb-0"
                        >
                            <template v-if="!file">
                                <WText
                                    tag="span"
                                    :size="14"
                                    weight="semi"
                                    ellipsis
                                    class="mb-0"
                                >
                                    {{ $t('imports.create.form.file.placeholderOne') }}
                                </WText>
                                <WText
                                    tag="span"
                                    :size="14"
                                    ellipsis
                                    class="mb-0"
                                >
                                    {{ $t('imports.create.form.file.placeholderTwo') }}
                                </WText>
                            </template>
                            <template>
                                {{ file }}
                            </template>
                        </WText>
                    </WFile>
                </WFieldError>
            </div>
        </div>

        <div class="Footer flex row-right-center">
            <WButton
                v-if="$DEV"
                :loading="isLoading"
                :disabled="isLoading"
                outline
                color="accent-blue"
                class="mr-2"
                @click="handleMock"
            >
                {{ $t('general.mock') }}
            </WButton>
            <WButton
                outline
                color="accent-blue"
                :loading="isLoading"
                @click="$emit('onReset')"
            >
                {{ $t('entities.creation.reset') }}
            </WButton>
            <WButton
                color="primary"
                :loading="isLoading"
                :disabled="isLoading"
                class="ml-2"
                @click="handleSubmit"
            >
                {{ $t('entities.creation.submit') }}
            </WButton>
        </div>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import fileDownload from 'js-file-download'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import {
    Api, apiRequest, rethrowError, notifyError,
} from '@/api'
import { SOURCES_DISCARD, SOURCES, SOURCES_WITHOUT_ENTITIES } from '@/entities/imports'

const FILE_EXTENSION = 'xlsx'
const FILE_OPTIONS = {
    headers: {
        'Content-Type': 'multipart/form-data',
    },
    timeout: 0,
}

const checkSource = source => SOURCES_WITHOUT_ENTITIES.includes(source)
const isMultiEntity = source => source === SOURCES.MULTI_ENTITY

const MOCK = {
    name: 'Demo name',
    source: 'manual',
    file: `Jeff Smith,2018,Prescott House,17-D,3.20`,
}

export default {
    name: 'ImportForm',
    mixins: [validationMixin],
    props: {
        accept: VueTypes.string.def('.csv'),
    },
    data () {
        return {
            form: {
                name: '',
                entity: '',
                source: '',
                file: '',
            },
            isLoading: false,
            isDownloading: false,

            entities: undefined,
            sources: undefined,
        }
    },
    computed: {
        disabledEntity () {
            return checkSource(this.form.source)
        },
        isMultiEntity () {
            return isMultiEntity(this.form.source)
        },
        fileAccept () {
            // Multi-entity requires Excel files with multiple sheets
            return this.isMultiEntity ? '.xlsx,.xlsm' : this.accept
        },
    },
    validations () {
        return {
            form: {
                entity: {
                    required: (value, data) => checkSource(data.source) || required(value),
                },
                source: { required },
                name: { required },
                file: { required },
            },
        }
    },
    beforeMount () {
        this.getTemplates()
        this.getSources()
    },
    methods: {
        handleFile ([file]) {
            this.form = { ...this.form, file }
        },
        handleDownloadTemplate () {
            this.downloadTemplate(this.form.entity, this.form.name)
        },
        handleChangeSource (source) {
            if (checkSource(source)) {
                this.form = { ...this.form, entity: '', file: '' }
            }
        },
        handleMock () {
            this.form = { ...MOCK, entity: this.entities[0].key }
        },
        handleSubmit () {
            this.$v.$touch()
            if (!this.$v.$invalid) {
                this.submitFile(this.form)
            }
        },
        getTemplates () {
            apiRequest(Api().templates.list())
                .then(({ data }) => (this.entities = data))
                .catch(rethrowError(notifyError()))
        },
        getSources () {
            apiRequest(Api().sources.list())
                .then(({ data }) => (this.sources = this.normalizeSources(data)))
                .catch(rethrowError(notifyError()))
        },
        normalizeSources (data) {
            return (data || [])
                .filter(({ key }) => !SOURCES_DISCARD.includes(key))
                .map(({ key, name }) => ({ key, label: name }))
        },
        async submitFile ({
            name, source, entity, file,
        }) {
            const params = new FormData()
            params.append('file', file)

            this.isLoading = true
            this.uploadFile(params, source)
                .then(fileUploadId => this.createImport({
                    name, entity, source, fileUploadId,
                }))
                .finally(() => (this.isLoading = false))
        },
        async uploadFile (params, source) {
            const uploadApi = isMultiEntity(source)
                ? Api().imports.filesMultiEntity(params, FILE_OPTIONS)
                : Api().imports.files(params, FILE_OPTIONS)

            return apiRequest(uploadApi)
                .then(({ data }) => Promise.resolve(data.id))
                .catch(rethrowError(notifyError()))
        },
        async createImport (params) {
            apiRequest(Api().imports.create(params))
                .then(({ data }) => this.$emit('onCreate', data))
                .catch(rethrowError(notifyError()))
        },
        async downloadTemplate (entity, name) {
            this.isDownloading = true
            apiRequest(Api().templates.download(entity, FILE_EXTENSION))
                .then((res) => { fileDownload(res, `${entity}_TEMPLATE.csv`) })
                .catch(rethrowError(notifyError()))
                .finally(() => { this.isDownloading = false })
        },
        handleDownloadMultiEntityTemplate () {
            this.isDownloading = true
            apiRequest(Api().templates.downloadMultiEntity())
                .then((res) => { fileDownload(res, 'multi_entity_template.xlsx') })
                .catch(rethrowError(notifyError()))
                .finally(() => { this.isDownloading = false })
        },
    },
}
</script>
<style lang="scss" scoped>
$form-width: 280px;

.Form {
    .WSelect {
        min-width: $form-width;
    }

    .WRangeSlider {
        margin-top: 25px;
    }

    .Range {
        position: absolute;
        top: 25px;
        width: 100%;
    }
}
.FormField {
    position: relative;
    max-width: $form-width;
}
</style>
